import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { UploadAsset, SaveTheme } from '../../../../utilities/DL/PersonalSurfer';
import { resolveAssetUrl } from '../../../../utilities/BL/PersonalSurfer';

/**
 * MODAL: PersonalSurferThemeModal
 * Creación y edición de temas visuales para Esquivador Personal (6 capas de assets + colores).
 */
export const PersonalSurferThemeModal = ({
    isOpen,
    onClose,
    theme = null,
    roomId,
    onSaved = () => {},
    triggerToast = () => {}
}) => {
    const isEdit = Boolean(theme?.id);

    const [name, setName] = useState('');
    const [primaryColor, setPrimaryColor] = useState('#00E5FF');
    const [secondaryColor, setSecondaryColor] = useState('#FF0055');
    const [brandLogoUrl, setBrandLogoUrl] = useState('');
    const [backgroundUrl, setBackgroundUrl] = useState('');
    const [roadUrl, setRoadUrl] = useState('');
    const [sidePropUrl, setSidePropUrl] = useState('');
    const [obstacleUrl, setObstacleUrl] = useState('');
    const [coinUrl, setCoinUrl] = useState('');
    const [bonusUrl, setBonusUrl] = useState('');
    const [playerUrl, setPlayerUrl] = useState('');

    const [uploadingField, setUploadingField] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (theme) {
            setName(theme.name || '');
            setPrimaryColor(theme.primaryColor || '#00E5FF');
            setSecondaryColor(theme.secondaryColor || '#FF0055');
            setBrandLogoUrl(theme.brandLogoUrl || '');
            setBackgroundUrl(theme.backgroundUrl || '');
            setRoadUrl(theme.roadUrl || '');
            setSidePropUrl(theme.sidePropUrl || '');
            setObstacleUrl(theme.obstacleUrl || '');
            setCoinUrl(theme.coinUrl || '');
            setBonusUrl(theme.bonusUrl || '');
            setPlayerUrl(theme.playerUrl || '');
        } else {
            setName('');
            setPrimaryColor('#00E5FF');
            setSecondaryColor('#FF0055');
            setBrandLogoUrl('');
            setBackgroundUrl('');
            setRoadUrl('');
            setSidePropUrl('');
            setObstacleUrl('');
            setCoinUrl('');
            setBonusUrl('');
            setPlayerUrl('');
        }
    }, [theme, isOpen]);

    if (!isOpen) return null;

    const handleFileUpload = async (e, fieldKey, assetType) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingField(fieldKey);
        try {
            const res = await UploadAsset(file, assetType, roomId);
            if (res.success && res.data?.url) {
                const url = res.data.url;
                if (fieldKey === 'brandLogo') setBrandLogoUrl(url);
                if (fieldKey === 'background') setBackgroundUrl(url);
                if (fieldKey === 'road') setRoadUrl(url);
                if (fieldKey === 'sideProp') setSidePropUrl(url);
                if (fieldKey === 'obstacle') setObstacleUrl(url);
                if (fieldKey === 'coin') setCoinUrl(url);
                if (fieldKey === 'bonus') setBonusUrl(url);
                if (fieldKey === 'player') setPlayerUrl(url);
                triggerToast('success', 'Asset subido y optimizado con éxito.');
            } else {
                triggerToast('error', res.message || 'Error al subir la imagen.');
            }
        } catch (err) {
            triggerToast('error', 'Error al procesar el archivo.');
        } finally {
            setUploadingField(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            triggerToast('warning', 'El nombre del tema es obligatorio.');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                id: theme?.id,
                roomId,
                name: name.trim(),
                primaryColor,
                secondaryColor,
                brandLogoUrl: brandLogoUrl || null,
                backgroundUrl: backgroundUrl || null,
                roadUrl: roadUrl || null,
                sidePropUrl: sidePropUrl || null,
                obstacleUrl: obstacleUrl || null,
                coinUrl: coinUrl || null,
                bonusUrl: bonusUrl || null,
                playerUrl: playerUrl || null
            };

            const res = await SaveTheme(payload, roomId);
            if (res.success) {
                triggerToast('success', isEdit ? 'Tema actualizado exitosamente.' : 'Tema creado exitosamente.');
                onSaved(res.data);
                onClose();
            } else {
                triggerToast('error', res.message || 'Error al guardar el tema.');
            }
        } catch (err) {
            triggerToast('error', 'Ocurrió un error al guardar.');
        } finally {
            setIsSaving(false);
        }
    };

    const assetFields = [
        { key: 'brandLogo', label: 'Logotipo de Patrocinador', value: brandLogoUrl, setter: setBrandLogoUrl, type: 'brandLogo', hint: '800x400 PNG con transparencia' },
        { key: 'background', label: 'Fondo del Lienzo (Background)', value: backgroundUrl, setter: setBackgroundUrl, type: 'background', hint: '1920x1080 WebP/JPG' },
        { key: 'road', label: 'Pista / Carretera (Road)', value: roadUrl, setter: setRoadUrl, type: 'road', hint: '1080x1920 WebP vertical' },
        { key: 'player', label: 'Personaje / Surfer (Player)', value: playerUrl, setter: setPlayerUrl, type: 'player', hint: '512x512 PNG con transparencia' },
        { key: 'obstacle', label: 'Obstáculo a Esquivar', value: obstacleUrl, setter: setObstacleUrl, type: 'obstacle', hint: '512x512 PNG con transparencia' },
        { key: 'coin', label: 'Moneda Coleccionable (+100 pts)', value: coinUrl, setter: setCoinUrl, type: 'coin', hint: '256x256 PNG con transparencia' },
        { key: 'bonus', label: 'Bonus / Power-up (+500 pts)', value: bonusUrl, setter: setBonusUrl, type: 'bonus', hint: '256x256 PNG con transparencia' },
        { key: 'sideProp', label: 'Accesorio Lateral Decorativo', value: sidePropUrl, setter: setSidePropUrl, type: 'sideProp', hint: '512x512 PNG con transparencia' }
    ];

    return (
        <div className="ps-modal-overlay">
            <div className="ps-modal-container ps-theme-modal">
                <div className="ps-modal-header">
                    <h3>
                        <i className="bi bi-palette-fill me-2" />
                        {isEdit ? 'Editar Tema Visual' : 'Crear Nuevo Tema Visual'}
                    </h3>
                    <button type="button" className="ps-close-btn" onClick={onClose}>
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="ps-modal-body">
                    {/* Información Básica */}
                    <div className="ps-form-group">
                        <label className="ps-label">Nombre del Tema *</label>
                        <input
                            type="text"
                            className="ps-input"
                            placeholder="Ej. Clásico Estadio, Modo Neón..."
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <label className="ps-label">Color Primario (HUD / Botones)</label>
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="color"
                                    className="form-control form-control-color ps-color-picker"
                                    value={primaryColor}
                                    onChange={(e) => setPrimaryColor(e.target.value)}
                                />
                                <input
                                    type="text"
                                    className="ps-input"
                                    value={primaryColor}
                                    onChange={(e) => setPrimaryColor(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <label className="ps-label">Color Secundario (Acentos)</label>
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="color"
                                    className="form-control form-control-color ps-color-picker"
                                    value={secondaryColor}
                                    onChange={(e) => setSecondaryColor(e.target.value)}
                                />
                                <input
                                    type="text"
                                    className="ps-input"
                                    value={secondaryColor}
                                    onChange={(e) => setSecondaryColor(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Grilla de Assets */}
                    <h5 className="ps-section-subtitle mb-2">Capas Gráficas del Juego</h5>
                    <div className="ps-asset-grid">
                        {assetFields.map((field) => (
                            <div key={field.key} className="ps-asset-card">
                                <label className="ps-label">{field.label}</label>
                                <span className="ps-hint">{field.hint}</span>

                                <div className="ps-asset-preview-box">
                                    {field.value ? (
                                        <img src={resolveAssetUrl(field.value)} alt={field.label} className="ps-preview-img" />
                                    ) : (
                                        <span className="ps-placeholder-text">Sin imagen</span>
                                    )}
                                </div>

                                <div className="ps-asset-actions">
                                    <label className="ps-upload-btn">
                                        <i className={`bi ${uploadingField === field.key ? 'bi-arrow-repeat spin' : 'bi-cloud-arrow-up-fill'} me-1`} />
                                        {uploadingField === field.key ? 'Subiendo...' : 'Subir'}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp"
                                            style={{ display: 'none' }}
                                            onChange={(e) => handleFileUpload(e, field.key, field.type)}
                                            disabled={uploadingField !== null}
                                        />
                                    </label>
                                    {field.value && (
                                        <button
                                            type="button"
                                            className="ps-btn-icon-danger"
                                            onClick={() => field.setter('')}
                                            title="Remover imagen"
                                        >
                                            <i className="bi bi-trash-fill" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="ps-modal-footer">
                        <button type="button" className="ps-btn ps-btn-secondary" onClick={onClose} disabled={isSaving}>
                            Cancelar
                        </button>
                        <button type="submit" className="ps-btn ps-btn-primary" disabled={isSaving || uploadingField !== null}>
                            <i className="bi bi-check2-circle me-1" />
                            {isSaving ? 'Guardando...' : isEdit ? 'Actualizar Tema' : 'Crear Tema'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

PersonalSurferThemeModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    theme: PropTypes.object,
    roomId: PropTypes.string,
    onSaved: PropTypes.func,
    triggerToast: PropTypes.func
};
